// src/components/quotes/QuoteModal.tsx
import React from 'react';
import { IQuote } from '../../types/Quotes';
import { XMarkIcon, CalendarIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, HomeModernIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import styles from './QuoteModal.module.css';

interface QuoteModalProps {
  quote: IQuote;
  onClose: () => void;
}

const QuoteModal: React.FC<QuoteModalProps> = ({ quote, onClose }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProjectTypeIcon = (type: string) => {
    return type === 'new' ? 
      <HomeModernIcon className={styles.typeIcon} /> : 
      <BuildingStorefrontIcon className={styles.typeIcon} />;
  };

  const getProjectTypeText = (type: string) => {
    return type === 'new' ? 'Nuevo Proyecto' : 'Remodelación';
  };

  const getCategoryText = (category: string) => {
    const categories: { [key: string]: string } = {
      'residencial': 'Residencial',
      'comercial': 'Comercial',
      'institucional': 'Institucional',
      'paisajismo': 'Paisajismo',
      'otros': 'Otros'
    };
    return categories[category] || category;
  };

  const getStatusBadge = (quote: IQuote) => {
    if (quote.isArchived) {
      return <span className={`${styles.statusBadge} ${styles.archived}`}>Archivada</span>;
    }
    if (!quote.isRead) {
      return <span className={`${styles.statusBadge} ${styles.unread}`}>Sin Leer</span>;
    }
    return <span className={`${styles.statusBadge} ${styles.read}`}>Leída</span>;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <h2 className={styles.modalTitle}>Detalles de Cotización</h2>
            {getStatusBadge(quote)}
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <XMarkIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Información del Cliente */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Información del Cliente</h3>
            <div className={styles.clientGrid}>
              <div className={styles.infoItem}>
                <strong>Nombre:</strong>
                <span>{quote.quoteName}</span>
              </div>
              <div className={styles.infoItem}>
                <strong>Email:</strong>
                <span className={styles.contactInfo}>
                  <EnvelopeIcon className={styles.contactIcon} />
                  {quote.quoteEmail}
                </span>
              </div>
              <div className={styles.infoItem}>
                <strong>Teléfono:</strong>
                <span className={styles.contactInfo}>
                  <PhoneIcon className={styles.contactIcon} />
                  {quote.quotePhone}
                </span>
              </div>
            </div>
          </section>

          {/* Información del Proyecto */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Información del Proyecto</h3>
            <div className={styles.projectGrid}>
              <div className={styles.infoItem}>
                <strong>Tipo de Proyecto:</strong>
                <span className={styles.typeInfo}>
                  {getProjectTypeIcon(quote.projectType)}
                  {getProjectTypeText(quote.projectType)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <strong>Categoría:</strong>
                <span>{getCategoryText(quote.projectCategory)}</span>
              </div>
              <div className={styles.infoItem}>
                <strong>Tamaño:</strong>
                <span>{quote.projectSize}</span>
              </div>
              <div className={styles.infoItem}>
                <strong>Ubicación:</strong>
                <span className={styles.locationInfo}>
                  <MapPinIcon className={styles.contactIcon} />
                  {quote.projectLocation}
                </span>
              </div>
            </div>
          </section>

          {/* Descripción del Proyecto */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Descripción del Proyecto</h3>
            <div className={styles.description}>
              {quote.projectDescription}
            </div>
          </section>

          {/* Información Adicional */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Información Adicional</h3>
            <div className={styles.additionalGrid}>
              <div className={styles.infoItem}>
                <strong>Fecha de Solicitud:</strong>
                <span className={styles.dateInfo}>
                  <CalendarIcon className={styles.contactIcon} />
                  {formatDate(quote.createdAt)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <strong>ID de Cotización:</strong>
                <span className={styles.quoteId}>#{quote.id}</span>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.closeActionButton}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;